import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { isPlatformBrowser, Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  PLATFORM_ID,
  QueryList,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  faChevronDown,
  faChevronUp,
  faLocationDot,
  faMap,
  faTimes,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { Accordion } from 'primeng/accordion';
import { Subject, take, takeUntil } from 'rxjs';

import { ENVIRONMENT, Environment } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { LocationService } from '@usheru-hff/sdk/location';
import { MovieService } from '@usheru-hff/sdk/movie';
import { TrackingService, SHOWTIMES_INTERSECTION_CONFIG } from '@usheru-hff/sdk/tracking';
import {
  Cinema,
  Country,
  getMovieGenres,
  getMovieTags,
  LocationComplete,
  Location as LocationModel,
  Movie,
  NearCity,
  PerformanceGroup
} from '@usheru-hff/shared/domain';
import { LocationSelectorComponent } from '@usheru-hff/shared/feature-location-selector';
import { ModalMapComponent } from '@usheru-hff/shared/ui-modal-map';
import { convertToISODate, dateFormatter, getDateFromDDMMYYYY } from '@usheru-hff/shared/utils';
import { ShowtimeStreamingService } from '@usheru-hff/shared/utils-movie';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

import { PerformanceComponent } from './performance/performance.component';
import { ShowtimeStreamingMapComponent } from './showtime-streaming-map/showtime-streaming-map.component';
import { ShowtimesStoreService } from '../shared/showtimes.service';

interface PanelEvent {
  show: Subject<void>;
  hide: Subject<void>;
}

interface Position {
  lat: number;
  lng: number;
}

/**
 * Component that show cinemas from a location where a movie in available.
 *
 * //TODO barbeitoalex propostal - refractor name to something like "showtime-selector".
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - move component folder to the root of components
 */
@Component({
  selector: 'ush-showtime-streaming-in-cinemas',
  templateUrl: './showtime-streaming-in-cinemas.component.html',
  styleUrls: ['./showtime-streaming-in-cinemas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [animate('300ms ease-in', style({ opacity: 0, transform: 'translateY(-20px)' }))])
    ]),
    trigger('listAnimation', [
      transition('* => *', [
        query(
          ':enter',
          [
            style({ opacity: 0, transform: 'translateY(60px)' }),
            stagger(100, [animate('0.5s ease-out', style({ opacity: 1, transform: 'translateY(0)' }))])
          ],
          { optional: true }
        )
      ])
    ])
  ]
})
export class ShowtimeStreamingInCinemasComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('wrapper') wrapperRef!: ElementRef<HTMLElement>;
  @ViewChild('modalMap') modalMap!: ElementRef<ModalMapComponent>;
  @ViewChild(LocationSelectorComponent, { read: ElementRef<HTMLElement> }) locationSelectorRef: ElementRef<HTMLElement>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('locationSelector') locationSelector: any;

  @ViewChild(Accordion) pAcordion: Accordion;
  @ViewChild(ShowtimeStreamingMapComponent) inlineComponentRef: ShowtimeStreamingMapComponent;

  /**
   * @docsNotRequired
   */
  @Input()
  set movie(movie: Movie) {
    this._movie = movie;
    this.showtimeStore.setMovie(movie);
    this.checkMovieIsOld();
  }

  get movie() {
    return this._movie;
  }

  _movie: Movie;

  @Input()
  enableRouterNavigation? = true;

  @Input()
  showUsheruLogo = false;

  errorKey = signal<string | undefined>(undefined);
  errorMsg = signal<string | undefined>(undefined);

  selectedLocationCountry: Country;

  /**
   * Counter to count chunks of performances by batches
   */
  loadMoreCtr: number = 0;

  /**
   * Batch Qty of Performances
   */
  loadQtyAtOnce: number = this.env.showtimeStreamingConfig?.numberOfCinemasOnFirstLoad || 5;

  /**
   * Boolean to check if all items have loaded on UI
   */
  allItemsVisible: boolean = false;

  /**
   * Emit the number of {@link performances} after get update this variable.
   */
  @Output()
  showsLoaded = new EventEmitter<number>();

  @Output()
  showtimeTicketing = new EventEmitter<number>();

  /**
   * @docsNotRequired
   */
  @ViewChildren(PerformanceComponent)
  panels: QueryList<PerformanceComponent>;

  @Input() openFirstPanel = false;

  /**
   * //TODO docs favio
   */
  @Input() inCinemasAddon?: TemplateRef<unknown>;

  _inlineMap = false;
  @Input()
  set inlineMap(inlineMap: boolean) {
    this._inlineMap = inlineMap;
    this.showtimeStore.setInlineMap(inlineMap);
  }
  get inlineMap(): boolean {
    return this._inlineMap;
  }

  /**
   * @deprecard Not necesary
   */
  params: Params;

  /**
   * Asociated to the actual {@link Router} query params.
   */
  queryParams: Params;

  /**
   * Items to set in the {@link PerformanceComponent}.
   */
  performances: PerformanceGroup[] = [];

  /**
   * Date selected in the UshShowtimeDatesComponent.
   */
  dateSelected: Date;

  /**
   * UI Items to set in the {@link PerformanceComponent}.
   */
  performancesUi: PerformanceGroup[] = [];

  /**
   * This count is need to add white space to replace showtime cards when date is changed {@link PerformanceComponent}.
   */
  previousPerformanceUiCount: number = 0;

  /**
   * @deprecard Don't found any use
   */
  panelEvents: PanelEvent[];

  /**
   * Status to know if it is waiting to update {@link performances}.
   */
  loadingShowtimes = true;

  /**
   * Status to know if there are some location loaded.
   */
  hasLocation = false;

  /**
   * @deprecard Don't found any use
   */
  cinema: Cinema;

  /**
   * @deprecard Don't found any use
   */
  indexPanelToOpen = -1;

  /**
   * Actual index reference {@link panels} opened.
   */
  currentIndex = -1;

  /**
   * Active if {@link Channel} has disableShowtimes.
   */
  disableShowtimes = false;

  useNewComponent = false;

  diasbleNearestCities: boolean;

  nearestCities: NearCity[] = [];

  /**
   * Used to track in developvolment the nearest cities changes.
   */
  initialPosition!: LocationModel;

  /**
   * Used to track in developvolment the nearest cities changes.
   */
  nearestCitySetted!: NearCity;

  /**
   * @docsNotRequired
   */
  defaultCinemaPlaceholder = 'https://cdnstatic.usheru.com/img/cinemas/cinemalogo-placeholder.jpg';

  locationIcon = faLocationDot;
  chevronDownIcon = faChevronDown;
  chevronUpIcon = faChevronUp;
  mapIcon = faMap;
  closeIncon = faTimes;
  markerIcon = faMapMarkerAlt;

  // NEW SHOWTIME FLOW
  public showTimeDates: { [key: string]: string } = null;
  showMap: boolean;

  @Input() newFlowEnabled: boolean = false;
  // NEW SHOWTIME FLOW

  hasMovieTwoYearsOld = signal(false);

  destroy$ = new Subject<void>();

  constructor(
    @Optional() private trackingService: TrackingService,
    @Inject(PLATFORM_ID) private platform,
    private channelService: ChannelService,
    private location: LocationService,
    private loc: Location,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private router: Router,
    private translationService: TranslationsService,
    private movieService: MovieService,
    private showtimeStore: ShowtimesStoreService,
    @Inject(ENVIRONMENT) private env: Environment,
    private showtimeStreamingService: ShowtimeStreamingService
  ) {
    this.disableShowtimes = channelService.channel.settings.disableShowtimes;
    this.diasbleNearestCities = this.env.showtimeStreamingConfig?.nearCitiesDisable;
    this.newFlowEnabled = this.env.showtimeStreamingConfig?.isNewFlow;
  }
  ngOnInit(): void {
    this.setInitialPosition();
    // if movie is an string then perform the call to backend for the actual movie
  }

  handleCinemaErrorImg(elem) {
    elem.target.src = this.defaultCinemaPlaceholder;
  }

  /**
   * * Listen actual query params.
   *
   * //TODO barbeitoalex propostal - why is this in the afterViewInit? Is good idea trigger this each url change?
   * //TODO barbeitoalex propostal - add the detectChanges to the method where is the problem.
   */
  ngAfterViewInit(): void {
    this.inCinemasAddon = this.inCinemasAddon || this.templatesService.get('inCinemasAddon');
    this.addShowtimesVisibilityListener();
    this.listenRouteParams();
    this.addonLog();
  }

  /**
   * @docsNotRequired
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes?.movie?.firstChange &&
      changes.movie?.currentValue.friendlyUrl != changes.movie?.previousValue.friendlyUrl
    ) {
      // this.restoreInitialPosition();
      this.listenSlug();
    }
  }

  private addShowtimesVisibilityListener(): void {
    if (!this.isBrowser || !this.trackingService) return;
    const { eventName, querySelector } = SHOWTIMES_INTERSECTION_CONFIG[this.env.appType];

    this.trackingService.usheruSdkTrack({
      kind: 'intersection',
      eventType: 'visibility',
      eventName,
      querySelector,
      payload: {
        event_category: 'MovieShowtimes',
        label: `${this.movie.title}`,
        movie: this.movie.title,
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie)
      }
    });
  }

  setInitialPosition() {
    this.nearestCitySetted = undefined;
    this.initialPosition = this.location.userLocation;
    this.selectedLocationCountry = this.location.selectedCountry;
  }

  listenRouteParams() {
    this.route.params.subscribe(params => {
      this.onUpdateParams(params);
    });
  }

  onUpdateParams(params: Params) {
    this.params = Object.assign(params, {});
    this.queryParams = Object.assign(this.route.snapshot.queryParams, {});
    this.listenSlug();
    this.cd.detectChanges();
  }

  /**
   * Check if there is a query param of cinema to load his location. If it is the case load performances from that
   * location.
   *
   * //TODO barbeitoalex propostal - refractor name. No observable or promise.
   */
  async listenSlug() {
    const cinemaSlug = this.queryParams.cinema;

    let position: Position;
    if (cinemaSlug && !this.disableShowtimes) {
      position = await this.cinemaLocation(cinemaSlug);
    }

    if (position) {
      this.hasLocation = true;
      this.loadPerformances(position);
      this.loadNearestCities(position);
    } else {
      this.checkLocation();
    }
  }

  /**
   * Get a {@link Position} from a cinema slug.
   *
   * //TODO barbeitoalex propostal - update to lastValueFrom
   * @param cinemaSlug
   * @returns
   */
  private async cinemaLocation(cinemaSlug: string): Promise<Position> {
    const cinema = await this.movieService
      .getCinema(cinemaSlug)
      .toPromise()
      .catch(() => {
        console.error(`No cinema with slug ${cinemaSlug}`);
      });

    if (cinema && cinema.latitude && cinema.longitude) {
      return {
        lat: cinema.latitude,
        lng: cinema.longitude
      };
    }
    return null;
  }

  /**
   * Gets user location from {@link LocationService} and start load performances from that.
   *
   * //TODO barbeitoalex propostal - refractor name. No observable or promise.
   */
  private checkLocation() {
    this.hasLocation = !!this.location.userLocation;
    if (this.location.userLocation) {
      const position = {
        lat: this.location.userLocation.latitude,
        lng: this.location.userLocation.longitude
      };
      this.loadPerformances(position);
      this.loadNearestCities(position);
    }
  }

  /**
   * Gets performances from a position.
   *
   * //TODO barbeitoalex propostal - use lastValueFrom.
   * @param position
   */
  private loadPerformances(position: Position) {
    this.loadingShowtimes = true;
    this.cd.detectChanges();

    const params = {
      movie: [(<Movie>this.movie)?.friendlyUrl],
      latitude: position.lat,
      longitude: position.lng,
      radius: this.channelService.channel.settings?.showtimeRadius || 50000
    };

    // Using Object.keys()
    if (this.env.performanceQueryExtraFilter) {
      Object.keys(this.env.performanceQueryExtraFilter).forEach(key => {
        const value = this.env.performanceQueryExtraFilter[key];
        params[key] = value;
      });
    }

    this.movieService
      .getPerformancesGroup(params)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(
        (performances = []) => {
          this.evaluateDistanceUnits(performances);
          this.performances = performances;
          this.addonLog();
          // NEW SHOWTIME FLOW
          this.newFlowEnabled && this.extractDatesForDateFilter(performances, this.env.performanceStartDateFilter);
          // NEW SHOWTIME FLOW
          if (this.env.performanceQueryResultFilter) {
            this.performances = this.performances.filter(perf => this.env.performanceQueryResultFilter(perf));
          }
          if (!this.newFlowEnabled) {
            this.resetLoadMoreVariables();
            this.loadMore();
          }
          // initialize the load counter
          this.panelEvents = [];
          this.panelEvents = performances.map((perfGroup, i) => {
            if (this.queryParams.cinema && this.queryParams.cinema === perfGroup.cinema.slug) {
              this.cinema = perfGroup.cinema;
              this.indexPanelToOpen = i;
              this.currentIndex = this.indexPanelToOpen;
            }
            return {
              show: new Subject(),
              hide: new Subject()
            };
          });

          if (this.performances && this.performances.length > 0) {
            this.showsLoaded.emit(this.performances.length);
          }

          if (this.performances.length == 0) {
            const error = this.showtimeStreamingService.getShowtimesErrorMessage(this.selectedLocationCountry);
            this.errorKey.set(error.key);
            this.errorMsg.set(error.message);
          }

          this.loadingShowtimes = false;
          this.performancesUi = performances;
          this.previousPerformanceUiCount = this.performancesUi.length;
          this.cd.detectChanges();
          if (this.openFirstPanel) {
            setTimeout(() => {
              this.pAcordion?.tabList.first.toggle(new MouseEvent('click'));
            }, 200);
          }
        },
        () => {
          this.loadingShowtimes = false;
          this.addonLog();
          this.cd.detectChanges();
        }
      );
  }

  // NEW SHOWTIME FLOW
  private extractDatesForDateFilter(performances: PerformanceGroup[], startPerformancesDate?: Date): void {
    let _tmpShowTimeDates = null;
    performances?.forEach((i: PerformanceGroup) => {
      if (i?.localTimes) {
        const localTimesArr = i.localTimes.split(',');

        localTimesArr?.forEach((j: string) => {
          const localFullDateStr = dateFormatter(j.split(' ')[0], 'dd-mm-yyyy', 'yyyy-mm-dd');
          const hourDateStr = j.split(' ')[1].replace(/(\d{1,2}:\d{2})(AM|PM)/g, '$1 $2');
          if (localFullDateStr) {
            if (!_tmpShowTimeDates) _tmpShowTimeDates = {};

            const isoDate: Date = convertToISODate(localFullDateStr, hourDateStr);

            // If a startPerformancesDate was provided, ignore any performance date before it
            if (startPerformancesDate && isoDate < startPerformancesDate) {
              return; // Skip this date
            }

            _tmpShowTimeDates[localFullDateStr] = isoDate.toLocaleDateString(
              this.translationService.currentLanguage().code,
              {
                weekday: 'short',
                day: '2-digit',
                month: 'short'
              }
            );
          }
        });
      }
    });
    if (_tmpShowTimeDates) {
      this.showTimeDates = { ..._tmpShowTimeDates };
    }
  }

  // NEW SHOWTIME FLOW

  private loadNearestCities(position: Position) {
    if (!this.diasbleNearestCities) {
      this.movieService
        .getNearestCities({
          movie: this.movie.id,
          latitude: position.lat,
          longitude: position.lng,
          radius: this.channelService.channel.settings?.showtimeRadius || 50000
        })
        .pipe(take(1))

        .subscribe(cities => {
          this.nearestCities = cities;
          this.addonLog();
          this.cd.detectChanges();
        });
    }
  }

  /**
   * @deprecard Don't found any use
   * @param location
   */
  handleLocationSelected(event: LocationComplete) {
    this.selectedLocationCountry = event.country;
    this.checkLocation();
    this.setInitialPosition();
  }

  get isBrowser(): boolean {
    return isPlatformBrowser(this.platform);
  }

  evaluateDistanceUnits(performances: PerformanceGroup[] = []) {
    performances.forEach(perf => {
      if (
        this.location?.selectedCountry?.slug == 'united-states' ||
        this.location?.selectedCountry?.slug == 'united-kingdom'
      ) {
        perf.distanceLiteral = (perf.distance * 0.00062137119).toFixed(2) + ' mi';
      } else {
        perf.distanceLiteral = (perf.distance / 1000).toFixed(2) + ' km';
      }
    });
  }

  onBuyTicketHandle(event) {
    this.showtimeTicketing.next(event);

    //TODO Tracking
  }

  /**
   * @deprecard Don't found any use
   * @param index
   */
  clickPanel(index: number) {
    this.panels.forEach((p, i) => {
      if (i !== index) {
        this.cinema = this.performances[i].cinema;
        this.panelEvents[i].hide.next();

        //TODO update this in master
      } else if (i !== this.indexPanelToOpen) {
        this.indexPanelToOpen = i;
      }
    });
  }

  /**
   * Updates tracking and url after a {@link PerformanceComponent} is open.
   *
   * //TODO barbeitoalex propostal - set event type
   * @param event
   */
  onCinemaOpened(event) {
    this.currentIndex = event.index;
    const openedCinema = this.performances[event.index].cinema;

    //TODO Tracking

    // this.router.navigate([], {
    //   relativeTo: this.route,
    //   queryParams: queryParams
    // });

    if (this.enableRouterNavigation) {
      const queryParams: Params = { cinema: openedCinema.slug };

      if (this.nearestCitySetted) {
        queryParams.lat = this.nearestCitySetted.latitude;
        queryParams.lon = this.nearestCitySetted.longitude;
      } else if (!this.location.userLocation?.userLoc) {
        queryParams.lat = this.location.userLocation?.latitude;
        queryParams.lon = this.location.userLocation?.longitude;
      }
      const url = this.router.createUrlTree([], { relativeTo: this.route, queryParams: queryParams }).toString();
      this.loc.go(url);
    }

    this.trackingService.trackEvent({
      action: 'SelectCinema',
      properties: {
        category: 'MovieShowtimes',
        label: `${this.movie.title} @ ${openedCinema.name}`,
        movie: this.movie.title,
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie),
        cinema: openedCinema.name,
        cinema_city: openedCinema.city,
        idCinema: openedCinema.id
      }
    });

    this.cd.detectChanges();
  }

  get thereArePerformances(): boolean {
    return !!(this.newFlowEnabled ? this.performancesUi?.length : this.performances?.length);
  }

  /**
   * @docsNotRequired
   * @param event
   */
  onCinemaClosed() {
    this.currentIndex = -1;
    this.cd.detectChanges();
  }

  onNearCitySelected(city: NearCity) {
    const queryParams = { ...this.route.snapshot.queryParams };
    queryParams.lat = city.latitude;
    queryParams.lng = city.longitude;
    this.nearestCitySetted = city;
    this.currentIndex = -1;
    this.loadPerformances(queryParams as Position);
    this.loadNearestCities(queryParams as Position);
    this.locationSelector.setCustomValue(`${city.name}, ${city.region.name}`);
    // update user location
    this.location.setUserLocation({
      latitude: city.latitude,
      longitude: city.longitude,
      address: `${city.name}, ${city.region.name}`
    });

    // track event
    this.trackingService.trackEvent({
      action: 'SelectCity',
      properties: {
        category: 'MovieShowtimes',
        label: `${this.movie.title} @ ${city.name}`,
        movie: this.movie.title,
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie)
      }
    });

    // check if the location selector is into the viewport
    const locationSelectorPosition = this.locationSelectorRef.nativeElement.getBoundingClientRect().top - 120;
    if (locationSelectorPosition < 0) {
      const wrapperPosition = this.wrapperRef.nativeElement.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: wrapperPosition, behavior: 'smooth' });
    }
    this.cd.detectChanges();
  }

  /**
   * Used before the {@link onNearCitySelected} update the user location.
   * Don't have any use right now.
   */
  restoreInitialPosition(loadPerformances = true) {
    this.nearestCitySetted = undefined;
    this.location.setUserLocation(this.initialPosition);
    if (loadPerformances) {
      this.currentIndex = -1;
      const { latitude: lat, longitude: lng } = this.initialPosition;
      this.loadPerformances({ lat, lng });
      this.loadNearestCities({ lat, lng });
      this.locationSelector.setCustomValue(this.initialPosition.address);
    }
  }

  private resetLoadMoreVariables() {
    this.loadMoreCtr = 0;
    this.allItemsVisible = false;
    this.cd.detectChanges();
  }

  public loadMore(): void {
    if (this.loadMoreCtr * this.loadQtyAtOnce < this.performances.length) this.loadMoreCtr += 1;
    if (this.loadMoreCtr * this.loadQtyAtOnce >= this.performances.length) this.allItemsVisible = true;
    this.cd.detectChanges();
  }

  get allCinemasForCurrentDateVisible(): boolean {
    return this.loadMoreCtr * this.loadQtyAtOnce >= this.performancesUi.length;
  }

  // NEW SHOWTIME FLOW
  updateCinemaListOnDateChange(dateString: string): void {
    // dateString expected to be 'yyyy-mm-dd'
    this.dateSelected = convertToISODate(dateString, `00:00:00`);

    this.performancesUi = [];
    setTimeout(() => {
      this.resetLoadMoreVariables();
      this.loadMore();
      this.performancesUi = this.performances.filter((i: PerformanceGroup) => {
        const allLocalTimes = i.localTimes
          .split(',')
          ?.map(i => dateFormatter(i.split(' ')[0], 'dd-mm-yyyy', 'yyyy-mm-dd'));
        return allLocalTimes.includes(dateString);
      });
      this.previousPerformanceUiCount = this.performancesUi?.length;
      this.cd.detectChanges();
    }, 0);
  }

  openMap(performance: PerformanceGroup) {
    performance.showMap.set(!performance.showMap());
  }

  performanceTrackBy(index: number, item: PerformanceGroup): number {
    return item.id;
  }

  checkMovieIsOld() {
    const release = getDateFromDDMMYYYY(this.movie?.locReleaseDate || this.movie?.releaseDate);
    if (!release) return false;
    const today = new Date();
    const releaseDate = new Date(release);
    const diffTime = today.getTime() - releaseDate.getTime();
    const diffYears = diffTime / (1000 * 3600 * 24 * 365.25);
    this.hasMovieTwoYearsOld.set(diffYears >= 2);
    this.addonLog();
  }

  /**
   * This won't be remove yet for testing propouses
   * in the future but need to be commented in prod
   */
  addonLog() {
    // const cineamo =
    //   this.inCinemasAddon !== undefined &&
    //   this.loadingShowtimes === false &&
    //   this.hasMovieTwoYearsOld() === true &&
    //   (this.performances == undefined || this.performances.length === 0) &&
    //   (this.nearestCities == undefined || this.nearestCities.length === 0);
    // console.log('Cineamo Log ' + cineamo, {
    //   inCinemasAddon: this.inCinemasAddon !== undefined,
    //   loadingShowtimes: this.loadingShowtimes === false,
    //   hasMovieTwoYearsOld: this.hasMovieTwoYearsOld() === true,
    //   performances: this.performances == undefined || this.performances.length === 0,
    //   nearestCities: this.nearestCities == undefined || this.nearestCities.length === 0
    // });
  }

  get templatesService() {
    return this.channelService.templatesService;
  }
}
